/**
 * ChunkPrioritizer.js - Handles prioritized loading of terrain chunks
 * 
 * This module determines which chunks should be loaded first based on:
 * 1. Distance from player
 * 2. Player view direction
 * 3. Current loading state
 */

import * as THREE from 'three';
import { LOADING_STATES } from './LoadingStates.js';

/**
 * Creates a chunk prioritizer that determines load order
 * @param {Object} config - Configuration object
 * @returns {Object} - Chunk prioritizer interface
 */
export function createChunkPrioritizer(config = {}) {
    // Default configuration
    const settings = {
        viewDirectionWeight: 0.5,   // How much to prioritize chunks in view direction (0-1)
        minimalChunksRadius: 1,     // Radius for minimal state
        playableChunksRadius: 2,    // Radius for playable state
        completeChunksRadius: 4,    // Full radius for complete state
        ...config
    };
    
    /**
     * Calculate chunk priorities around a position
     * @param {Object} position - Player position {x,y,z}
     * @param {Object} viewDirection - Camera direction vector {x,y,z}
     * @param {Object} loadingState - Current loading state tracker
     * @returns {Array} Array of chunks with priority values
     */
    function prioritizeChunks(position, viewDirection, loadingState, chunkSize = 32) {
        // Get chunks based on current loading state
        let radius;
        switch (loadingState.state) {
            case LOADING_STATES.NOT_STARTED:
            case LOADING_STATES.TITLE_READY:
                radius = settings.minimalChunksRadius;
                break;
            case LOADING_STATES.MINIMAL_LOADED:
                radius = settings.playableChunksRadius;
                break;
            case LOADING_STATES.PLAYABLE:
                radius = settings.completeChunksRadius;
                break;
            default:
                radius = settings.completeChunksRadius;
        }
        
        // Calculate center chunk
        const centerChunkX = Math.floor(position.x / chunkSize);
        const centerChunkZ = Math.floor(position.z / chunkSize);
        
        // Normalize view direction
        const viewDir = new THREE.Vector3(viewDirection.x, 0, viewDirection.z).normalize();
        
        const chunksWithPriority = [];
        
        // Generate chunks with priorities
        for (let dx = -radius; dx <= radius; dx++) {
            for (let dz = -radius; dz <= radius; dz++) {
                const chunkX = centerChunkX + dx;
                const chunkZ = centerChunkZ + dz;
                
                // Calculate straight-line distance (circular priority)
                const distSq = dx * dx + dz * dz;
                
                // Skip chunks outside the circular radius
                if (distSq > radius * radius) continue;
                
                // Base priority is inverse of distance squared (closer = higher priority)
                const distancePriority = 1 / (1 + distSq);
                
                // Calculate view direction priority
                // Create vector from player to chunk center
                const chunkCenterX = (chunkX * chunkSize) + (chunkSize / 2);
                const chunkCenterZ = (chunkZ * chunkSize) + (chunkSize / 2);
                const toChunkDir = new THREE.Vector3(
                    chunkCenterX - position.x,
                    0,
                    chunkCenterZ - position.z
                ).normalize();
                
                // Dot product gives priority to chunks in view direction
                const dotProduct = viewDir.dot(toChunkDir);
                const viewPriority = (dotProduct + 1) / 2; // Convert from [-1,1] to [0,1]
                
                // Calculate overall priority (blend of distance and view direction)
                const priority = distancePriority * (1 - settings.viewDirectionWeight) +
                               viewPriority * settings.viewDirectionWeight;
                               
                // Add to priority list
                chunksWithPriority.push({
                    x: chunkX,
                    z: chunkZ,
                    priority,
                    key: `${chunkX},${chunkZ}`,
                    distance: Math.sqrt(distSq)
                });
            }
        }
        
        // Sort by priority (highest first)
        chunksWithPriority.sort((a, b) => b.priority - a.priority);
        
        return chunksWithPriority;
    }
    
    /**
     * Get chunk load radius for current state
     * @param {Object} loadingState - Current loading state
     * @returns {number} Chunk radius to load
     */
    function getRadiusForState(loadingState) {
        switch (loadingState.state) {
            case LOADING_STATES.NOT_STARTED:
            case LOADING_STATES.TITLE_READY:
                return settings.minimalChunksRadius;
            case LOADING_STATES.MINIMAL_LOADED:
                return settings.playableChunksRadius;
            case LOADING_STATES.PLAYABLE:
                return settings.completeChunksRadius;
            default:
                return settings.completeChunksRadius;
        }
    }
    
    return {
        prioritizeChunks,
        getRadiusForState,
        settings
    };
}

// MOBILE-FUTURE: Add mobile-specific prioritization with smaller radius and 
// higher priority for nearest chunks to optimize for mobile performance 